import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Settings } from '@garage/shared'
import { api } from './api.js'
import { type Formatters, makeFormatters } from './format.js'
import { qk } from './query-keys.js'

/**
 * Settings are read by almost every screen (currency, VAT rate, feature
 * toggles), change rarely, and are cheap. A long staleTime keeps them out of
 * the network path without any manual caching.
 */
export function useSettings() {
  return useQuery({
    queryKey: qk.settings,
    queryFn: () => api.settings.get(),
    staleTime: 5 * 60 * 1000,
  })
}

/** Money, quantity and date formatters bound to the current settings. */
export function useFormatters(): Formatters {
  const { data } = useSettings()
  return useMemo(() => makeFormatters(data), [data])
}

/** Convenience for the feature toggles, with safe defaults before load. */
export function useFeatures(): Pick<Settings, 'whatsappEnabled' | 'showMargin' | 'appointmentsEnabled' | 'accountingMode'> & { advancedAccounting: boolean } {
  const { data } = useSettings()
  const accountingMode = data?.accountingMode ?? 'simplified'
  return {
    whatsappEnabled: data?.whatsappEnabled ?? false,
    showMargin: data?.showMargin ?? false,
    appointmentsEnabled: data?.appointmentsEnabled ?? false,
    accountingMode,
    advancedAccounting: accountingMode === 'advanced',
  }
}

/** Debounce a rapidly-changing value, for search boxes. */
export function useDebounced<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

/**
 * Warn before leaving with unsaved changes. Covers the browser-level unload
 * only; in-app navigation is guarded by the editor itself, since React Router
 * blockers and this event are separate mechanisms.
 */
export function useUnsavedChangesWarning(dirty: boolean): void {
  const dirtyRef = useRef(dirty)
  useEffect(() => {
    dirtyRef.current = dirty
  }, [dirty])
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])
}
